<script setup>
import { ref, reactive, onMounted, defineEmits } from 'vue'
import { helpers, required } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import dayjs from 'dayjs'
import errorHandler from '@/helpers/errorhandler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanPersediaanBarangService from '@/services/LaporanPersediaanBarangService'
import ObatService from '@/services/ObatService'
import PabrikService from '@/services/PabrikService'
import GolonganObatService from '@/services/GolonganObatService'
import useVuelidate from '@vuelidate/core'
import { saveAs } from 'file-saver'

const toast = useToast()
const laporanService = new LaporanPersediaanBarangService()
const obatService = new ObatService()
const pabrikService = new PabrikService()
const golonganObatService = new GolonganObatService()
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingExport = ref(false)
let isLoading = ref(false)
let acObat = ref([])
let form = reactive({
  date_from: null,
  date_to: null,
  obat: 0,
  pabrik: 0,
  golongan: 0,
  harga: 0,
})
let submitted = ref(false)
let list_pabrik = ref([])
let list_golongan = ref([])

const rules = {
  obat: {
    required: helpers.withMessage('Obat harus diisi.', required),
  },
  pabrik: {
    required: helpers.withMessage('Pabrik harus diisi.', required),
  },
  golongan: {
    required: helpers.withMessage('Golongan harus diisi.', required),
  },
  date_from: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
  date_to: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

onMounted(async () => {
  const pabrik = await pabrikService.get('?sort=nama')
  list_pabrik.value = pabrik.data.data

  const result = []
  const golongan = await golonganObatService.get()
  golongan.data.data.forEach((el) => {
    if (el.golongan_sub && el.golongan_sub.length === 0) {
      result.push({
        id: el.id,
        nama: el.nama,
      })
    } else {
      el.golongan_sub.forEach((sub) => {
        result.push({
          id: sub.id,
          nama: sub.nama,
        })
      })
    }
  })

  list_golongan.value = result
})

function searchObat(e) {
  obatService
    // .get(`?filter[nama]=${e.query}&filter[status]=aktif`)
    .getByName(`?filter[nama]=${e.query}`)
    .then((res) => {
      acObat.value = res.data.data
    })
    .catch((err) => {
      errorHandler(err, 'Data Obat', toast)
    })
}

function search(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  onExport()
}

function onExport() {
  isLoadingExport.value = true
  const startDate = dayjs(form.date_from).format('YYYY-MM-DD')
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')
  const obatId = form.obat.id
  const pabrikId = form.pabrik
  const golonganId = form.golongan
  const harga = form.harga

  isLoading.value = true
  laporanService
    .exportLaporanMutasi(
      startDate,
      endDate,
      obatId,
      pabrikId,
      golonganId,
      harga
    )
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'laporan-mutasi-stok-per-barang.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Data Laporan Mutasi Stok ', toast)
    })
    .finally(() => ((isLoadingExport.value = false), (isLoading.value = false)))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

function resetForm() {
  form.date_from = null
  form.date_to = null
  form.obat = 0
  form.pabrik = 0
  form.golongan = 0
  form.harga = 0
}
</script>

<template>
  <fx-report-container
    title="Laporan Mutasi Stok"
    :loading="isLoadingExport"
    @toggle="onToggle"
  >
    <template #content>
      <form @submit.prevent="search(!v$.$invalid)">
        <div class="formgrid grid surface-ground m-1 py-3">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-4">
            <AutoComplete
              v-model="v$.obat.$model"
              :min-length="2"
              :suggestions="acObat"
              field="nama"
              class="w-full"
              placeholder="Obat"
              input-class="w-full"
              autofocus
              @complete="searchObat($event)"
            >
              <template #item="slotProps">
                <div>
                  {{ slotProps.item.kode }} - {{ slotProps.item.nama }}<br />
                </div>
              </template>
              <small
                v-if="
                  (v$.obat.$invalid && submitted) || v$.obat.$pending.$response
                "
                class="p-error"
                >{{ v$.obat.required.$message }}</small
              >
            </AutoComplete>
          </div>
          <div class="field col-12 md:col-4">
            <Dropdown
              v-model="v$.pabrik.$model"
              :options="list_pabrik"
              optionValue="id"
              optionLabel="nama"
              placeholder="Pilih Pabrik"
              class="w-full"
              filter
            />
            <small
              v-if="
                (v$.pabrik.$invalid && submitted) ||
                v$.pabrik.$pending.$response
              "
              class="p-error"
              >{{ v$.pabrik.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-4">
            <Dropdown
              v-model="v$.golongan.$model"
              :options="list_golongan"
              :class="{
                'p-invalid': v$.golongan.$invalid && submitted,
              }"
              optionValue="id"
              optionLabel="nama"
              placeholder="Pilih Golongan"
              class="w-full"
              filter
            />
            <small
              v-if="
                (v$.golongan.$invalid && submitted) ||
                v$.golongan.$pending.$response
              "
              class="p-error"
              >{{ v$.golongan.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              v-model="v$.date_from.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_from.$invalid && submitted,
              }"
              placeholder="Tanggal (dari)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_from.$invalid && submitted) ||
                v$.date_from.$pending.$response
              "
              class="p-error"
              >{{ v$.date_from.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              v-model="v$.date_to.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_to.$invalid && submitted,
              }"
              placeholder="Tanggal (s/d)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_to.$invalid && submitted) ||
                v$.date_to.$pending.$response
              "
              class="p-error"
              >{{ v$.date_to.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-4 xl:col-2 flex xl:align-items-center">
            <div class="flex align-items-center" style="height: fit-content;">
              <InputSwitch
                v-model="form.harga"
                :true-value="1"
                :false-value="0"
              />
              <span class="text-sm ml-2">Tampilkan harga</span>
            </div>
          </div>
          <div class="field col-12 md:col-3">
            <Button type="submit" icon="pi pi-file-pdf" label="Cetak" class="mb-0 md:mb-2 xl:mb-0" />
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined ml-2 md:ml-0 xl:ml-2"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
